<template>
  <div class="report-preview">
    <SaveOrDiscardDialog v-if="dialog" v-model="dialog">
      <template #content>
        <span v-if="!newReport.title"
          >Your changes will not be saved. If you want to save changes please enter a name:</span
        >
        <span v-else>Do you want to save changes ?</span>

        <q-input v-if="!newReport.title" v-model.trim="reportUpdatedName" label="Report name" dense />
      </template>

      <template #actions>
        <q-btn outline label="Cancel" color="tertiary" @click="dialogCancelled" />

        <q-btn outline label="Discard" color="red" @click="discardChanges" />

        <q-btn
          :loading="isSaveBtnLoading"
          :disable="!reportUpdatedName && !newReport.title"
          outline
          label="Save"
          color="green"
          @click="saveChanges"
        />
      </template>
    </SaveOrDiscardDialog>

    <div class="table">
      <div class="col-xs-12">
        <AgGridWrapper
          ref="agGrid"
          :options="gridOptions"
          :infinite-scroll-request="infiniteScrollRequest"
          :update-rows-count="updateRowsCount"
          @drag-stopped="columnDragStopped"
        >
          <template #header-row>
            <TableHeader
              :is-save-btn-loading="isSaveBtnLoading"
              :report-name.sync="reportName"
              @save="save()"
              @generate="generate()"
            />
          </template>
          <template #footerActions>
            <div class="row fit q-pr-md q-pb-md justify-end">Total Rows: {{ totalRows }}</div>
          </template>
        </AgGridWrapper>
      </div>
    </div>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import * as apiTo from '@api/api-routes'
import { cloneDeep } from 'lodash'
import { previewReportMixin } from '@/modules/reports/shared/preview-report-mixin'
// Components
import AgGridWrapper from '@components/ag-grid-helpers/AgGridWrapper'
import TableHeader from '@/modules/reports/components/TableHeader'
import SaveOrDiscardDialog from '@/modules/reports/components/SaveOrDiscardDialog'

export default {
  components: {
    AgGridWrapper,
    TableHeader,
    SaveOrDiscardDialog
  },

  mixins: [previewReportMixin],

  props: {
    id: [Number, String] // from route
  },

  data: () => ({
    dialog: false,
    routeBeforeDialog: null,
    reportUpdatedName: null
  }),

  created() {
    this.setRouterView('newReportPage')
    this.setShowBuildingReportBtn(true)
    this.setShowReportColumns(true)
    window.onbeforeunload = () => {
      return true
    }
  },

  beforeRouteLeave(to, from, next) {
    this.guardRoute(to, from, next)
  },

  beforeRouteUpdate(to, from, next) {
    this.guardRoute(to, from, next, () => {
      const reportTypes = this.$store.state.reports.reportTypes
      const report = reportTypes.find(r => r.slug === to.params.slug)
      this.$store.commit('reports/setNewReport', report)
    })
  },

  beforeDestroy() {
    window.onbeforeunload = null
  },

  methods: {
    ...mapMutations('reports', [
      'setNewReport',
      'addSavedReport',
      'setVisibleColumns',
      'updateNewReport',
      'setShowBuildingReportBtn',
      'setShowReportColumns',
      'setRouterView'
    ]),

    guardRoute(to, from, next, cb) {
      if (!this.routeBeforeDialog) {
        this.routeBeforeDialog = to
        this.dialog = true
      } else {
        this.setShowBuildingReportBtn(false)
        this.setShowReportColumns(false)
        cb && cb()
        next()
      }
    },

    infiniteScrollRequest(request) {
      return apiTo.getReportTypePreview(this.newReport.id, {
        ...request,
        selectedColumns: this.selectedColumns()
      })
    },

    dialogCancelled() {
      this.dialog = false
      this.routeBeforeDialog = null
    },

    discardChanges() {
      this.$router.push(this.routeBeforeDialog)
    },

    saveChanges() {
      if (this.reportUpdatedName) {
        const report = cloneDeep(this.newReport)
        report.title = this.reportUpdatedName
        this.reportUpdatedName = null
        this.updateNewReport(report)
      }
      this.save(() => {
        this.$router.push(this.routeBeforeDialog)
      })
    },

    save(callback) {
      const savePayload = this.getSaveReportPayload()

      this.isSaveBtnLoading = true
      apiTo
        .saveReport({
          report_type_id: this.newReport.id,
          title: this.reportName,
          ...savePayload
        })
        .then(({ data }) => {
          const report = data.result
          this.addSavedReport(report)
          if (!callback) {
            this.routeBeforeDialog = `/reports/${report.report_type.slug}/${report.id}/edit`
            this.$router.push(this.routeBeforeDialog)
          }
        })
        .finally(() => {
          this.isSaveBtnLoading = false
          if (callback) callback()
        })
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/modules/reports/shared/styles.styl'

agGridTableStyle()
</style>
